<template>
  <div>
    <SectionTitle
      id="assistants-info-section"
      title="Información de Asistentes de Servicio"
      class="mt-4 mb-2"
      :has-line="true"
    />
    <AssistantsHeaderFilter
      :selected-assistant.sync="selectedAssistant"
      :assistants="availableAssistantOptions"
      @add-assistant="onAddAssistant"
    />
    <CustomSeparator
      v-if="hasSelectedAssistants"
      id="assistants-section"
      text="Asistentes Seleccionados"
      class="my-4"
    />
    <AssistantResultRow
      v-for="(assistant, index) in selectedAssistants"
      :key="assistant.id"
      :value="assistant"
      @remove="onRemoveAssistant(index)"
    />
    <Pagination
      v-if="hasSelectedAssistants && pagination.totalItemCount > pagination.perPage"
      :data="pagination"
      :page-size="pagination.perPage"
      parent-id="assistants-section"
      @pagination-go-page="fetchSelectedAssistants"
    />
  </div>
</template>

<script>
import Pagination from 'pagination';
import SectionTitle from 'SectionTitle';
import CustomSeparator from 'custom-separator';
import AssistantsHeaderFilter from './AssistantsHeaderFilter';
import AssistantResultRow from './AssistantResultRow';
import alert from '@/mixin/alert.mixin';
import {
  addAssistantToTeacher,
  getAlternativeLocationTeacherAssistants,
  getAvailableAssistants,
  removeAssistantFromTeacher,
} from '@/modules/alternative-location-management/api';

export default {
  name: 'AssistantsSection',
  mixins: [alert],
  components: {
    Pagination,
    SectionTitle,
    CustomSeparator,
    AssistantsHeaderFilter,
    AssistantResultRow,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        data: [],
      }),
    },
    alternativeLocationTeacherId: {
      type: [Number, String],
      required: true,
    },
    academicYearId: {
      type: [Number, String],
      required: true,
    },
    schoolId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      selectedAssistant: null,
      assistantOptions: [],
    };
  },
  computed: {
    selectedAssistants: {
      get() {
        return this.value?.data || [];
      },
      set(value) {
        this.$emit('input', {
          ...this.pagination,
          data: value,
          count: value.length,
        });
      },
    },
    pagination() {
      return {
        perPage: this.value?.perPage || 10,
        currentPage: this.value?.currentPage || 1,
        totalItemCount: this.value?.totalItemCount || 0,
        pageCount: this.value?.pageCount || 0,
        count: this.value?.count || 0,
        data: this.value?.data || [],
      };
    },
    availableAssistantOptions() {
      const selectedAssistantIds = this.selectedAssistants.map(
        (assistant) => assistant.assistantId
      );

      return this.assistantOptions.filter(
        (assistant) => !selectedAssistantIds.includes(assistant.assistantId)
      );
    },
    hasSelectedAssistants() {
      return this.pagination.totalItemCount > 0;
    },
  },
  methods: {
    async fetchAvailableAssistants() {
      if (!this.academicYearId || !this.schoolId) return;

      const { data } = await getAvailableAssistants({
        academicYearId: this.academicYearId,
        schoolId: this.schoolId,
        alternativeLocationTeacherId: this.alternativeLocationTeacherId,
      });

      this.assistantOptions = data || [];
    },
    async fetchSelectedAssistants(page = 1) {
      if (
        !this.alternativeLocationTeacherId ||
        !this.academicYearId ||
        !this.schoolId
      )
        return;

      const { data } = await getAlternativeLocationTeacherAssistants({
        alternativeLocationTeacherId: this.alternativeLocationTeacherId,
        academicYearId: this.academicYearId,
        schoolId: this.schoolId,
        pageIndex: page,
        pageSize: this.pagination.perPage,
      });

      this.$emit('input', data);
    },
    async onAddAssistant() {
      if (!this.selectedAssistant) return;

      const assistantExists = this.selectedAssistants.some(
        (assistant) =>
          assistant.assistantId === this.selectedAssistant.assistantId
      );
      if (!assistantExists) {
        await addAssistantToTeacher({
          alternativeLocationTeacherId: this.alternativeLocationTeacherId,
          assistantId: this.selectedAssistant.assistantId,
        });

        await this.fetchSelectedAssistants(1);
        await this.fetchAvailableAssistants();
      }
      this.selectedAssistant = null;
    },
    async onRemoveAssistant(index) {
      const assistant = this.selectedAssistants[index];
      const response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover el asistente ${assistant.name}?`
      );

      if (!response) return;

      await removeAssistantFromTeacher({
        alternativeLocationTeacherId: this.alternativeLocationTeacherId,
        assistantId: assistant.assistantId,
      });

      const nextPage =
        this.selectedAssistants.length === 1 && this.pagination.currentPage > 1
          ? this.pagination.currentPage - 1
          : this.pagination.currentPage;
      await this.fetchSelectedAssistants(nextPage);
      await this.fetchAvailableAssistants();
    },
  },
  watch: {
    academicYearId: {
      immediate: true,
      handler() {
        this.fetchAvailableAssistants();
      },
    },
    schoolId() {
      this.fetchAvailableAssistants();
    },
  },
};
</script>
